import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { BaseBlock } from "@/react/components/shared/BuilderUI/DataModel.types";
import type { IdResponseData } from "@circle-react/api/broadcastsApi";
import { broadcastsApi } from "@circle-react/api/broadcastsApi";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import type { BroadcastStatusValue } from "../components/Broadcasts/constants";
import type {
  CommunitySegmentAttributes,
  EmailContentAttributes,
  ScheduleOptions,
} from "./useGetBroadcast";
import { getBroadcastQueryKey } from "./useGetBroadcast";

interface UseUpdateBroadcastParams {
  id: number;
}

export interface PartialBroadcastBuilderFormFields {
  broadcast_content?: {
    visual_email_builder_content?: BaseBlock;
    visual_email_builder_content_html?: string;
    email_content?: EmailContentAttributes;
    email_content_html?: string;
    preview_text?: string;
    subject?: string;
    updated_at?: string;
  };
  community_segment?: CommunitySegmentAttributes;
  disable_save_email_template_prompt?: boolean | null;
  email_hub_theme_id?: number | null;
  internal_name?: string;
  marketing_email_template_id?: number | null;
  marketing_email_template_name?: string | null;
  scheduled_at?: string | null;
  send_type?: ScheduleOptions | "";
  status?: BroadcastStatusValue;
}

export interface MutationParams {
  body: PartialBroadcastBuilderFormFields;
}

export const useUpdateBroadcast = ({ id }: UseUpdateBroadcastParams) => {
  const queryClient = useQueryClient();
  const toastr = useToast();

  return useMutation<IdResponseData, ApiError, MutationParams>({
    mutationFn: ({ body }) =>
      broadcastsApi.update({
        id,
        body: {
          internal_name: body.internal_name,
          broadcast_content_attributes: body.broadcast_content,
          community_segment_attributes: body.community_segment,
          scheduled_at: body.scheduled_at,
          send_type: body.send_type,
          email_hub_theme_id: body.email_hub_theme_id,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries(getBroadcastQueryKey(id));
    },
    onError: error => {
      if (error.message) {
        return toastr.error(error.message);
      }

      toastr.error(t("unexpected_error"));
    },
  });
};
